import React from 'react';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import { alpha, lighten, useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import './ContextMenu.scss';

export type ContextMenuProps = {
  contextMenu: {
    mouseX: number | null;
    mouseY: number | null;
    childIds: string[];
    selectedId: string | null;
    isFile?: boolean;
    file_type: string;
    folder_type: string;
    section: string;
  };
  handleCloseMenu: () => void;
  /** Asks to delete the row; omit to hide the Delete item. */
  handleOpenDeleteModal?: () => void;
  /** Opens a file's edit dialog; omit to hide the Edit item. */
  handleOpenEditModal?: () => void;
  /** Starts inline rename for a folder; omit to hide the Rename item. */
  handleRenameFolder?: () => void;
};

/**
 * Desktop-style row menu: a rounded raised panel inset by 4px, a single icon
 * column, and the destructive action set apart below a separator.
 *
 * Every colour is read from the Material theme, which the file explorer builds
 * from the active Joy colour scheme — so the menu follows light and dark
 * without a second palette to keep in step. The previous literals (a #555E68
 * label on a #F0F4F8 hover) turned near-invisible once the surface went dark.
 *
 * Each action shows only when its handler is given, so the host decides what
 * the user may do; with none to offer, the menu is not drawn at all. It opens
 * towards the reading direction: rightwards from the point, or leftwards in a
 * right-to-left language.
 */
const ContextMenu: React.FC<ContextMenuProps> = ({
  contextMenu,
  handleCloseMenu,
  handleOpenDeleteModal,
  handleOpenEditModal,
  handleRenameFolder,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Only show context menu for original files
  if (
    (contextMenu.isFile && contextMenu.file_type !== 'original') ||
    contextMenu.folder_type === 'consolidated' ||
    contextMenu.folder_type === 'summary'
  ) {
    return null;
  }

  const showEdit = Boolean(contextMenu.isFile && handleOpenEditModal);
  const showRename = !contextMenu.isFile && Boolean(handleRenameFolder);
  const showDelete = Boolean(handleOpenDeleteModal);

  if (!showEdit && !showRename && !showDelete) return null;

  const horizontal = i18n.dir() === 'rtl' ? 'right' : 'left';

  const itemSx: SxProps<Theme> = {
    minHeight: 34,
    px: 1,
    py: 0.5,
    borderRadius: 1,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.5,
    color: 'text.primary',
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: 'action.hover',
    },
  };

  const destructiveItemSx: SxProps<Theme> = {
    ...itemSx,
    color: 'error.main',
    '&:hover, &.Mui-focusVisible': {
      // A red wash rather than a solid fill: legible on either surface, and it
      // keeps the item readable while it is the one under the cursor.
      backgroundColor: alpha(theme.palette.error.main, isDark ? 0.18 : 0.09),
      color: 'error.main',
    },
  };

  return (
    <Menu
      className="explorer-context-menu"
      open={contextMenu.mouseY !== null}
      onClose={handleCloseMenu}
      anchorReference="anchorPosition"
      anchorPosition={
        contextMenu.mouseY !== null && contextMenu.mouseX !== null
          ? {
              top: contextMenu.mouseY + 8,
              left: contextMenu.mouseX,
            }
          : undefined
      }
      transformOrigin={{ vertical: 'top', horizontal }}
      transitionDuration={120}
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            minWidth: 200,
            p: 0.5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            // In dark mode the theme's paper is nearly black, which reads as a
            // hole rather than a panel — lift the surface off the background.
            backgroundColor: isDark
              ? lighten(theme.palette.background.paper, 0.08)
              : theme.palette.background.paper,
            backgroundImage: 'none',
            color: 'text.primary',
            boxShadow: isDark
              ? '0 8px 24px rgba(0, 0, 0, 0.55)'
              : '0 8px 24px rgba(16, 24, 40, 0.14)',
          },
        },
        list: { dense: true, sx: { p: 0 } },
      }}
    >
      {showEdit && (
        <MenuItem disableRipple onClick={handleOpenEditModal} sx={itemSx}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          {t('Edit')}
        </MenuItem>
      )}

      {showRename && (
        <MenuItem disableRipple onClick={handleRenameFolder} sx={itemSx}>
          <ListItemIcon>
            <DriveFileRenameOutlineIcon fontSize="small" />
          </ListItemIcon>
          {t('Rename')}
        </MenuItem>
      )}

      {(showEdit || showRename) && showDelete && <Divider sx={{ my: 0.5 }} />}

      {showDelete && (
        <MenuItem
          disableRipple
          onClick={handleOpenDeleteModal}
          sx={destructiveItemSx}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          {t('Delete')}
        </MenuItem>
      )}
    </Menu>
  );
};

export { ContextMenu };
